import type { Control, UseFormRegister } from "react-hook-form";
import { Controller } from "react-hook-form";

import type { AddressInformationFormType } from "@/types";
import { ComboboxWithLabels, FormField, Input } from "@/ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/ui/common/Accordion";
import { STATE_OPTIONS } from "@/utils";

type AddressFieldPath = "billingAddress" | "shippingAddress";

interface AddressErrorMessages {
  addressLineOne?: string;
  addressLineTwo?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}
interface AddressAccordionProps {
  title: string;
  fieldPath: AddressFieldPath;
  registerFn: UseFormRegister<AddressInformationFormType>;
  control: Control<AddressInformationFormType>;
  errorMessages: AddressErrorMessages;
  defaultOpen?: boolean;
}

export function AddressAccordion({
  title,
  fieldPath,
  registerFn,
  control,
  errorMessages,
  defaultOpen = false,
}: AddressAccordionProps) {
  return (
    <Accordion
      type="single"
      collapsible
      defaultValue={defaultOpen ? fieldPath : ""}
      className="col-span-2"
    >
      <AccordionItem value={fieldPath}>
        <AccordionTrigger className="bg-neutral-50 px-4 py-1">
          {title}
        </AccordionTrigger>
        <AccordionContent className="grid grid-cols-2">
          <FormField
            label="Address Line 1"
            error={errorMessages.addressLineOne}
            hasAsterisk
          >
            <Input
              placeholder="10 Bramble Bush Rd"
              {...registerFn(`${fieldPath}.addressLineOne`)}
              error={errorMessages.addressLineOne}
            />
          </FormField>
          <FormField
            label="Address Line 2"
            error={errorMessages.addressLineTwo}
          >
            <Input
              placeholder="Apt 1A"
              {...registerFn(`${fieldPath}.addressLineTwo`)}
              error={errorMessages.addressLineTwo}
            />
          </FormField>
          <FormField label="City" error={errorMessages.city} hasAsterisk>
            <Input
              placeholder="E.g. Springfield"
              {...registerFn(`${fieldPath}.city`)}
              error={errorMessages.city}
            />
          </FormField>
          <FormField label="State" error={errorMessages.state} hasAsterisk>
            <Controller
              control={control}
              name={`${fieldPath}.state`}
              render={({ field, fieldState }) => (
                <ComboboxWithLabels
                  options={STATE_OPTIONS}
                  value={field.value}
                  onChange={field.onChange}
                  hasError={!!fieldState.error?.message}
                  placeholder="Select or search state"
                />
              )}
            />
          </FormField>
          <FormField label="Zip Code" error={errorMessages.zipCode} hasAsterisk>
            <Input
              type="number"
              placeholder="02540"
              {...registerFn(`${fieldPath}.zipCode`)}
              error={errorMessages.zipCode}
            />
          </FormField>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
